import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store.ts'
import { msalInstance } from '@/auth/msal'
import AppLayout from '@/views/AppLayout.vue'
import type { RoleName } from '@/auth/roles.ts'
import { PAGE_ROLE_MAP } from '@/auth/page-role-map.ts'

// define routes
const routes = [
  {
    path: '/forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
  },
  { path: '/login', name: 'Login', component: () => import('@/views/login/LoginView.vue') },
  {
    path: '/registration',
    name: 'Registration',
    component: () => import('@/views/register/SendRegistrationView.vue'),
  },
  {
    path: '/registration/accept',
    name: 'RegistrationAccept',
    component: () => import('@/views/register/RegistrationView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: AppLayout,
    meta: { breadcrumb: 'Home', roles: PAGE_ROLE_MAP.home },
    children: [
      {
        path: '',
        name: 'Start',
        component: () => import('@/views/home/HomeView.vue'),
        meta: { breadcrumb: 'Start' },
      },
      { path: '/logout', name: 'Logout', component: { render: () => null } },
      {
        path: 'users',
        component: () => import('@/views/users/UsersWrapperView.vue'),
        meta: { breadcrumb: 'Benutzer', roles: PAGE_ROLE_MAP.users },
        children: [
          {
            path: '',
            redirect: 'list',
          },
          {
            path: 'list',
            name: 'Users',
            component: () => import('@/views/users/UsersListView.vue'),
          },
          {
            path: 'add',
            name: 'UsersAdd',
            meta: { breadcrumb: 'Benutzer hinzufügen' },
            component: () => import('@/views/users/UsersAddOrEditView.vue'),
          },
          {
            path: ':userId',
            name: 'UserDetails',
            meta: { breadcrumb: 'Benutzer bearbeiten' },
            component: () => import('@/views/users/UsersAddOrEditView.vue'),
            props: true,
          },
        ],
      },
      {
        path: 'creditors',
        component: () => import('@/views/creditors/CreditorsWrapperView.vue'),
        meta: { breadcrumb: 'Rechnungsstellen', roles: PAGE_ROLE_MAP.creditors },
        children: [
          {
            path: '',
            redirect: 'list',
          },
          {
            path: 'list',
            name: 'Creditors',
            component: () => import('@/views/creditors/CreditorListView.vue'),
          },
          {
            path: 'add',
            name: 'CreditorsAdd',
            meta: { showFooterActions: true, breadcrumb: 'Rechnungsstelle hinzufügen' },
            component: () => import('@/views/creditors/CreditorAddOrEditView.vue'),
          },
          {
            path: ':creditorId',
            name: 'CreditorsEdit',
            meta: { showFooterActions: true, breadcrumb: 'Rechnungsstelle bearbeiten' },
            component: () => import('@/views/creditors/CreditorAddOrEditView.vue'),
            props: true,
          },
        ],
      },
      {
        path: 'claims',
        component: () => import('@/views/claims/ClaimsWrapperView.vue'),
        meta: { breadcrumb: 'Forderungen', roles: PAGE_ROLE_MAP.claims },
        children: [
          {
            path: '',
            redirect: 'list',
          },
          {
            path: 'list',
            name: 'Claims',
            component: () => import('@/views/claims/ClaimListView.vue'),
          },
          {
            path: 'new/:debtorId?',
            name: 'ClaimsNew',
            meta: { showFooterActions: true, breadcrumb: 'Neue Forderung' },
            component: () => import('@/views/claims/ClaimCreationView.vue'),
          },
          {
            path: ':claimId',
            name: 'ClaimDetails',
            meta: { showFooterActions: true, breadcrumb: 'Forderung bearbeiten' },
            component: () => import('@/views/claims/ClaimDetailsView.vue'),
            props: true,
          },
        ],
      },
      {
        path: 'debtors',
        component: () => import('@/views/debtors/DebtorsWrapperView.vue'),
        meta: { breadcrumb: 'Schuldner', roles: PAGE_ROLE_MAP.debtors },
        children: [
          {
            path: '',
            redirect: 'list',
          },
          {
            path: 'list',
            name: 'Debtors',
            component: () => import('@/views/debtors/DebtorsListView.vue'),
          },
          {
            path: 'add',
            name: 'DebtorsAdd',
            meta: { showFooterActions: true, breadcrumb: 'Schuldner hinzufügen' },
            component: () => import('@/views/debtors/DebtorAddOrEditView.vue'),
          },
          {
            path: ':debtorId',
            name: 'DebtorDetails',
            meta: { showFooterActions: true, breadcrumb: 'Schuldner bearbeiten' },
            component: () => import('@/views/debtors/DebtorAddOrEditView.vue'),
            props: true,
          },
        ],
      },
      {
        path: 'notifications',
        component: () => import('@/views/notifications/NotificationsWrapperView.vue'),
        meta: { breadcrumb: 'Benachrichtigungen', roles: PAGE_ROLE_MAP.notifications },
        children: [
          {
            path: '',
            redirect: 'list',
          },
          {
            path: 'list',
            name: 'Notifications',
            component: () => import('@/views/notifications/NotificationsListView.vue'),
          },
        ],
      },
      {
        path: 'workbasket',
        component: () => import('@/views/workbasket/WorkBasketWrapperView.vue'),
        meta: { breadcrumb: 'Arbeitskorb', roles: PAGE_ROLE_MAP.workBasket },
        children: [
          {
            path: '',
            redirect: 'list',
          },
          {
            path: 'list',
            name: 'WorkBasket',
            component: () => import('@/views/workbasket/WorkBasketListView.vue'),
          },
          {
            path: ':claimId',
            name: 'WorkBasketDetails',
            meta: { breadcrumb: 'Einzelansicht für Forderung' },
            component: () => import('@/views/workbasket/WorkBasketDetailsView.vue'),
            props: true,
          },
        ],
      },
      {
        path: 'correspondenz',
        component: () => import('@/views/workbasket/WorkBasketCorrespondenzWrapperView.vue'),
        meta: { breadcrumb: 'Korrespondenz', roles: PAGE_ROLE_MAP.correspondenz },
        children: [
          {
            path: '',
            redirect: 'list',
          },
          {
            path: 'list',
            name: 'Correspondenz',
            component: () => import('@/views/workbasket/WorkBasketCorrespondenzListView.vue'),
          },
        ],
      },
      {
        path: 'backofficeadmin',
        component: () => import('@/views/workbasket/WorkBasketAdminWrapperView.vue'),
        meta: { breadcrumb: 'Administration', roles: PAGE_ROLE_MAP.backofficeAdmin },
        children: [
          {
            path: '',
            redirect: 'main',
          },
          {
            path: 'main',
            name: 'BackofficeAdmin',
            component: () => import('@/views/workbasket/WorkBasketAdminView.vue'),
          },
        ],
      },
      {
        path: 'base-data',
        name: 'BaseData',
        component: () => import('@/views/base-data/BaseDataView.vue'),
        meta: { showFooterActions: true, breadcrumb: 'Meine Daten', roles: PAGE_ROLE_MAP.baseData },
      },
      {
        path: 'invitation',
        name: 'Invitation',
        meta: { hidePanel: true, breadcrumb: 'Benutzer registrieren' },
        children: [
          {
            path: '',
            redirect: 'accept',
          },
          {
            path: 'accept',
            name: 'InvitationAccept',
            component: () => import('@/views/invitation/InvitationView.vue'),
          },
        ],
      },
      {
        path: 'faq',
        name: 'FAQ',
        component: () => import('@/views/faq/FaqView.vue'),
        meta: { breadcrumb: 'Häufig gestellte Fragen', roles: PAGE_ROLE_MAP.faq },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/login',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// ---------------------------------------------------------------------------------------------------------------------
// authentication
// ---------------------------------------------------------------------------------------------------------------------

// set up auth and guard routes
router.beforeEach(async (to) => {
  const auth = useAuthStore()

  const publicRoutes = ['/login', '/registration', '/registration/accept', '/invitation', '/invitation/accept']

  if (publicRoutes.includes(to.path)) {
    return true
  }

  if (to.path === '/logout') {
    await auth.signOut()
    return false
  }

  const account = msalInstance.getActiveAccount()

  if (!account) {
    return {
      path: '/login',
      query: { redirect: to.fullPath },
    }
  }

  await auth.refreshRoles()

  if (to.path === '/' && auth.hasAnyRole(['ARAG', 'ARAG_ADMIN'])) {
    return { path: '/workbasket/list' }
  }

  const requiredRoles = to.meta.roles as RoleName[] | undefined

  if (requiredRoles && !auth.hasAnyRole(requiredRoles)) {
    return { path: '/forbidden' }
  }

  return true
})

// ---------------------------------------------------------------------------------------------------------------------
// per-page body class
// ---------------------------------------------------------------------------------------------------------------------

// Tracks the page class currently applied to <body> so it can be removed on the next navigation.
let currentPageBodyClass: string | null = null

function toKebabCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase()
}

// After each navigation, replace the body class with one for the current page.
// An explicit `meta.bodyClass` wins; otherwise the class is derived from the route name.
router.afterEach((to) => {
  if (currentPageBodyClass) {
    document.body.classList.remove(currentPageBodyClass)
    currentPageBodyClass = null
  }

  const explicit = to.meta.bodyClass as string | undefined
  const routeName = (to.name ?? [...to.matched].reverse().find((record) => record.name)?.name)
    ?.toString()

  const pageClass = explicit ?? (routeName ? `page-${toKebabCase(routeName)}` : null)

  if (pageClass) {
    document.body.classList.add(pageClass)
    currentPageBodyClass = pageClass
  }
})

// export
export default router
